import { RESIDENT_STATE, SENSOR_TYPE, type Sensor, SensorKey } from "@packages/core";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { type ReactNode, useMemo, useRef } from "react";
import { LuClipboardCopy } from "react-icons/lu";
import { type Merge } from "type-fest";
import {
  useBedInfiniteQuery,
  useLatestStatusQuery,
  useResidentQuery,
  useTranslate,
} from "../../services";
import { css, cx } from "../../styled-system/css";
import { TableStyle, Tooltip, TypographyStyle, skeletonStyle } from "../common/index";
import { copyToClipboard } from "../utilities";

const classes = TableStyle.raw({ foot: false });

export type SensorTableProperties = Merge<
  React.ComponentProps<"div">,
  {
    action?: { (sensor: Sensor): ReactNode };
    columns?: Readonly<
      Partial<
        Record<"action" | "bed" | "name" | "resident" | "sensorKey" | "state" | "type", boolean>
      >
    >;
    loading?: boolean;
    rows?: ReadonlyArray<Sensor>;
  }
>;

export function SensorTable({
  action,
  className,
  columns,
  loading,
  rows = [],
  ...properties
}: SensorTableProperties) {
  const translate = useTranslate();

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize() {
      return 64;
    },
    getScrollElement() {
      return parentRef.current;
    },
    overscan: 20,
  });

  const gridTemplateColumns = useMemo(
    function () {
      return [
        columns?.sensorKey !== false ? "104px" : "",
        columns?.type !== false ? "40px" : "",
        columns?.name !== false ? "minmax(240px, 1fr)" : "",
        columns?.bed !== false ? "64px" : "",
        columns?.resident !== false ? "120px" : "",
        columns?.state !== false ? "80px" : "",
        columns?.action !== false ? "64px" : "",
      ]
        .filter(function (column) {
          return column !== "";
        })
        .join(" ");
    },
    [columns],
  );

  const headCellClass = TypographyStyle({ color: "primary/60", ellipsis: true, fontWeight: 500 });

  return (
    <div
      {...properties}
      className={cx(
        css(classes.root, TypographyStyle.raw({ fontSize: "14px", lineHeight: 1 })),
        className,
      )}
    >
      <div className={css(classes.head)}>
        <div className={css(classes.headRow)} style={{ gridTemplateColumns }}>
          {columns?.sensorKey !== false && (
            <div className={headCellClass}>{translate("%SensorKey.0%")}</div>
          )}
          {columns?.type !== false && <div className={headCellClass}>{translate("%Type.0%")}</div>}
          {columns?.name !== false && (
            <div className={headCellClass}>{translate("%SensorName.0%")}</div>
          )}
          {columns?.bed !== false && <div className={headCellClass}>{translate("%Bed.0%")}</div>}
          {columns?.resident !== false && (
            <div className={headCellClass}>{translate("%Resident.0%")}</div>
          )}
          {columns?.state !== false && (
            <div className={headCellClass}>{translate("%Status.0%")}</div>
          )}
          {columns?.action !== false && <div />}
        </div>
      </div>

      <div className={css(classes.virtualOuterBody)} ref={parentRef}>
        <div
          className={css(classes.virtualInnerBody)}
          style={{ height: virtualizer.getTotalSize() + "px" }}
        >
          {virtualizer.getVirtualItems().map(function (virtualItem) {
            const sensor = rows[virtualItem.index];
            return (
              <div
                className={css(classes.virtualBodyRow)}
                data-index={virtualItem.index}
                key={virtualItem.key}
                ref={virtualizer.measureElement}
                style={{
                  gridTemplateColumns,
                  height: virtualItem.size + "px",
                  transform: "translateY(" + virtualItem.start + "px)",
                }}
              >
                <div>
                  <Tooltip
                    content={
                      <div className={css({ display: "flex", alignItems: "center", gap: "8px" })}>
                        <LuClipboardCopy className={css({ height: "14px", width: "auto" })} />
                        <span
                          className={css(TypographyStyle.raw({ ellipsis: true, monospace: true }), {
                            backgroundColor: "muted-background",
                          })}
                        >
                          {sensor.sensorKey}
                        </span>
                      </div>
                    }
                    trigger={
                      <span
                        className={css(TypographyStyle.raw({ ellipsis: true, monospace: true }), {
                          cursor: "pointer",
                          fontWeight: 400,
                          _hover: { fontWeight: 500, textDecoration: "underline" },
                          _active: { fontWeight: 700 },
                        })}
                        onClick={function () {
                          copyToClipboard(sensor.sensorKey);
                        }}
                      >
                        {sensor.sensorKey}
                      </span>
                    }
                  />
                </div>
                <div>{translate("%SensorType." + sensor.type + ".0%")}</div>
                <div>
                  {sensor.name}
                  <span className={css({ color: "gray", fontSize: "10px" })}>
                    &nbsp;
                    {sensor.type === SENSOR_TYPE.AAMS
                      ? "(" + sensor.macAddress + ")"
                      : sensor.type === SENSOR_TYPE.ANSIEL
                        ? "(" + sensor.serialNumber + ")"
                        : null}
                  </span>
                </div>
                <BedCell sensorKey={sensor.sensorKey} />
                <ResidentCell sensorKey={sensor.sensorKey} />
                <StatusCell sensorKey={sensor.sensorKey} />
                <div>{action?.(sensor)}</div>
              </div>
            );
          })}
        </div>

        {rows.length === 0 &&
          (loading ? (
            Array.from({ length: 5 }).map(function (_, index) {
              return (
                <div
                  className={css(classes.bodyRow, { padding: "16px" })}
                  key={index}
                  style={{ gridTemplateColumns }}
                >
                  <div className={css(skeletonStyle, { maxWidth: "96px" })} />
                  <div className={css(skeletonStyle, { maxWidth: "128px" })} />
                  <div />
                </div>
              );
            })
          ) : (
            <div className={css(classes.bodyRow, { padding: "16px" })}>
              <p className={css({ fontWeight: 500 })}>{translate("%NoSensors.0%")}</p>
            </div>
          ))}
      </div>
    </div>
  );
}

type BedCellProperties = Merge<React.ComponentProps<"div">, { sensorKey: SensorKey }>;
function BedCell({ sensorKey, ...properties }: BedCellProperties) {
  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "sensorKey-gsi",
    condition: [["sensorKey", "=", sensorKey]],
  });

  const bed = bedInfiniteQuery.data?.pages.at(0)?.beds.at(0);

  return (
    <div {...properties}>
      {bed && (
        <span>
          {bed?.roomName}&nbsp;-&nbsp;{bed.bedName}
        </span>
      )}
    </div>
  );
}

type ResidentCellProperties = Merge<React.ComponentProps<"div">, { sensorKey: SensorKey }>;
function ResidentCell({ sensorKey, ...properties }: ResidentCellProperties) {
  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "sensorKey-gsi",
    condition: [["sensorKey", "=", sensorKey]],
  });

  const bed = bedInfiniteQuery.data?.pages.at(0)?.beds.at(0);

  const residentQuery = useResidentQuery(bed?.userKey);

  return <div {...properties}>{residentQuery.data?.name}</div>;
}

type StatusCellProperties = Merge<React.ComponentProps<"div">, { sensorKey: SensorKey }>;
function StatusCell({ sensorKey, ...properties }: StatusCellProperties) {
  const translate = useTranslate();

  const { data: status } = useLatestStatusQuery(sensorKey);

  const state = useMemo(
    function () {
      return !status || status.state === RESIDENT_STATE.UNKNOWN
        ? null
        : status.state === RESIDENT_STATE.PROVIDER_ERROR ||
            status.state === RESIDENT_STATE.SENSOR_ERROR
          ? false
          : true;
    },
    [status],
  );

  return (
    <div {...properties}>
      {status && (
        <span
          className={css({
            color: "white",
            borderRadius: "65535px",
            padding: "2px 10px",
          })}
          style={{
            backgroundColor: state === true ? "#0D9488" : state === false ? "#DC2626" : "#71717A",
          }}
        >
          {state === true && translate("%Normal.0%")}
          {state === false && translate("%Abnormal.0%")}
          {state === null && translate("%Unknown.0%")}
        </span>
      )}
    </div>
  );
}
